import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, CheckCircle2, XCircle, Phone, CalendarCheck, HandshakeIcon, ChevronLeft, ChevronRight } from 'lucide-react';
type Goal = {
  id: string;
  title: string;
  current: number;
  target: number;
  icon: string;
};
type DayData = {
  date: number;
  isPast: boolean;
  isPerfect: boolean;
  goals: Goal[];
};
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const generateMonthData = (month: number, year: number): DayData[] => {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const now = new Date();
  const isCurrentMonth = month === now.getMonth() && year === now.getFullYear();
  const today = isCurrentMonth ? now.getDate() : new Date(year, month, 1) < now ? daysInMonth + 1 : 0;
  const data: DayData[] = [];
  for (let i = 1; i <= daysInMonth; i++) {
    const isPast = i < today;
    const seed = year * 10000 + month * 100 + i;
    const pseudoRandom = (seed * 9301 + 49297) % 233280 / 233280;
    const isPerfect = isPast ? pseudoRandom > 0.4 : false;
    const goalRandom = (offset: number) => {
      const s = seed + offset;
      return (s * 9301 + 49297) % 233280 / 233280;
    };
    data.push({
      date: i,
      isPast,
      isPerfect,
      goals: [{
        id: '1',
        title: 'Get 100 dials',
        current: isPast ? isPerfect ? 100 : Math.floor(goalRandom(1) * 60) + 40 : 0,
        target: 100,
        icon: 'phone'
      }, {
        id: '2',
        title: 'Set 15 appointments',
        current: isPast ? isPerfect ? 15 : Math.floor(goalRandom(2) * 10) + 5 : 0,
        target: 15,
        icon: 'calendar'
      }, {
        id: '3',
        title: 'Close 5 loans this month',
        current: isPast ? isPerfect ? 5 : Math.floor(goalRandom(3) * 4) : 0,
        target: 5,
        icon: 'handshake'
      }]
    });
  }
  return data;
};
export function MonthTotals({
  onNavigate
}: {
  onNavigate: (view: 'daily') => void;
}) {
  const [selectedDay, setSelectedDay] = useState<DayData | null>(null);
  const [month, setMonth] = useState(new Date().getMonth());
  const [year, setYear] = useState(new Date().getFullYear());
  const monthData = useMemo(() => generateMonthData(month, year), [month, year]);
  // Compute the day of week the 1st falls on (0=Sun, 6=Sat)
  const startOffset = new Date(year, month, 1).getDay();
  const emptyDays = Array.from({
    length: startOffset
  }, (_, i) => i);
  const pastDays = monthData.filter(d => d.isPast);
  const perfectDaysCount = pastDays.filter(d => d.isPerfect).length;
  const goToPrevMonth = () => {
    setSelectedDay(null);
    if (month === 0) {
      setMonth(11);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
  };
  const goToNextMonth = () => {
    setSelectedDay(null);
    if (month === 11) {
      setMonth(0);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
  };
  return <div className="w-full max-w-5xl mx-auto p-6 pt-12 md:pt-20" data-id="element-39">
      {/* Header */}
      <div className="mb-8" data-id="element-40">
        <button onClick={() => onNavigate('daily')} className="flex items-center gap-2 text-gray-500 hover:text-[#1B6B3A] mb-6 transition-colors" data-id="element-41">
          <ArrowLeft size={18} data-id="element-42" />
          Back to Today
        </button>

        <div className="flex items-center gap-4 mb-2" data-id="element-43">
          <button onClick={goToPrevMonth} className="p-2 rounded-lg hover:bg-[#1B6B3A]/10 text-gray-500 hover:text-[#1B6B3A] transition-colors" data-id="element-44">
            <ChevronLeft size={24} data-id="element-45" />
          </button>
          <h1 className="text-3xl font-bold text-[#1B6B3A] tracking-tight min-w-[240px] text-center" data-id="element-46">
            {MONTH_NAMES[month]} {year}
          </h1>
          <button onClick={goToNextMonth} className="p-2 rounded-lg hover:bg-[#1B6B3A]/10 text-gray-500 hover:text-[#1B6B3A] transition-colors" data-id="element-47">
            <ChevronRight size={24} data-id="element-48" />
          </button>
        </div>
        <p className="text-gray-500 text-lg" data-id="element-49">
          {pastDays.length === 0 ? 'No past days to show yet.' : `${perfectDaysCount} of ${pastDays.length} past days were perfect.`}
        </p>
      </div>

      {/* Side by side layout */}
      <div className="flex flex-col lg:flex-row gap-6" data-id="element-50">
        {/* Calendar Grid */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200/60 lg:flex-1" data-id="element-51">
          <div className="grid grid-cols-7 gap-2 mb-4" data-id="element-52">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => <div key={day} className="text-center text-sm font-medium text-gray-400 py-2" data-id="element-53">
                {day}
              </div>)}
          </div>

          <div className="grid grid-cols-7 gap-2" data-id="element-54">
            {emptyDays.map(i => <div key={`empty-${i}`} className="aspect-square rounded-xl bg-transparent" data-id="element-55" />)}

            {monthData.map(day => <button key={day.date} onClick={() => day.isPast && setSelectedDay(day)} disabled={!day.isPast} className={`aspect-square rounded-xl flex flex-col items-center justify-center relative transition-all
                  ${!day.isPast ? 'bg-gray-50 text-gray-300 cursor-default' : 'hover:bg-gray-50 cursor-pointer border border-gray-100'}
                  ${selectedDay?.date === day.date ? 'ring-2 ring-[#1B6B3A] ring-offset-2' : ''}
                `} data-id="element-56">
                <span className={`text-sm font-medium mb-1 ${!day.isPast ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-57">
                  {day.date}
                </span>
                {day.isPast && <div className="mt-1" data-id="element-58">
                    {day.isPerfect ? <CheckCircle2 size={20} className="text-[#1B6B3A]" data-id="element-59" /> : <XCircle size={20} className="text-red-400" data-id="element-60" />}
                  </div>}
              </button>)}
          </div>
        </div>

        {/* Day Detail Panel */}
        <div className="lg:w-80 lg:flex-shrink-0" data-id="element-61">
          <AnimatePresence mode="wait" data-id="element-62">
            {selectedDay ? <motion.div key={`${month}-${selectedDay.date}`} initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200/60 lg:sticky lg:top-6" data-id="element-63">
                <div className="flex items-center justify-between mb-6" data-id="element-64">
                  <h3 className="text-lg font-bold text-gray-900" data-id="element-65">
                    {MONTH_NAMES[month]} {selectedDay.date}, {year}
                  </h3>
                  <div className={`px-3 py-1 rounded-full text-xs font-medium ${selectedDay.isPerfect ? 'bg-[#1B6B3A]/10 text-[#1B6B3A]' : 'bg-red-50 text-red-600'}`} data-id="element-66">
                    {selectedDay.isPerfect ? 'Perfect Day' : 'Missed'}
                  </div>
                </div>

                <div className="space-y-4" data-id="element-67">
                  {selectedDay.goals.map(goal => {
                const isCompleted = goal.current >= goal.target;
                const progressPercentage = Math.min(100, goal.current / goal.target * 100);
                return <div key={goal.id} className="p-3 rounded-xl bg-gray-50 border border-gray-100" data-id="element-68">
                        <div className="flex items-center gap-3 mb-2" data-id="element-69">
                          <div className={`p-2 rounded-lg ${isCompleted ? 'bg-[#1B6B3A]/10 text-[#1B6B3A]' : 'bg-white text-gray-400 shadow-sm'}`} data-id="element-70">
                            {goal.icon === 'phone' && <Phone size={16} data-id="element-71" />}
                            {goal.icon === 'calendar' && <CalendarCheck size={16} data-id="element-72" />}
                            {goal.icon === 'handshake' && <HandshakeIcon size={16} data-id="element-73" />}
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-74">
                            <span className={`text-sm font-medium block truncate ${isCompleted ? 'text-gray-900' : 'text-gray-700'}`} data-id="element-75">
                              {goal.title}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3" data-id="element-76">
                          <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-77">
                            <div className={`h-full rounded-full ${isCompleted ? 'bg-[#1B6B3A]' : 'bg-gray-400'}`} style={{
                        width: `${progressPercentage}%`
                      }} data-id="element-78" />
                          </div>
                          <span className={`text-xs font-medium whitespace-nowrap ${isCompleted ? 'text-[#1B6B3A]' : 'text-gray-500'}`} data-id="element-79">
                            {goal.current}/{goal.target}
                          </span>
                        </div>
                      </div>;
              })}
                </div>
              </motion.div> : <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200/60 flex items-center justify-center text-center lg:sticky lg:top-6 min-h-[200px]" data-id="element-80">
                <p className="text-gray-400 text-sm" data-id="element-81">
                  Click a past day to see its results
                </p>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </div>;
}